import React, { createContext, useCallback, useContext, useState } from 'react'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
}

const MAX_TOASTS = 5
const DEFAULT_DURATION = 3000

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID()
      const duration = toast.duration ?? DEFAULT_DURATION

      setToasts((prev) => {
        // Maintain max 5 toasts by removing oldest if needed
        const newToasts = [...prev, { ...toast, id, duration }]
        if (newToasts.length > MAX_TOASTS) {
          return newToasts.slice(newToasts.length - MAX_TOASTS)
        }
        return newToasts
      })

      // Auto-dismiss after duration
      setTimeout(() => {
        removeToast(id)
      }, duration)
    },
    [removeToast]
  )

  const success = useCallback(
    (message: string, duration?: number) => {
      addToast({ type: 'success', message, duration })
    },
    [addToast]
  )

  const error = useCallback(
    (message: string, duration?: number) => {
      addToast({ type: 'error', message, duration })
    },
    [addToast]
  )

  const warning = useCallback(
    (message: string, duration?: number) => {
      addToast({ type: 'warning', message, duration })
    },
    [addToast]
  )

  const info = useCallback(
    (message: string, duration?: number) => {
      addToast({ type: 'info', message, duration })
    },
    [addToast]
  )

  const value: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  }

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}
